/**
 * Wait Time Cache
 * In-memory (5 min TTL) + localStorage (30 min TTL) with stale-while-revalidate.
 */
import { WaitTimeResult } from './types';

const MEMORY_TTL_MS = 5 * 60 * 1000;     // 5 minutes
const STORAGE_TTL_MS = 30 * 60 * 1000;   // 30 minutes
const STORAGE_KEY_PREFIX = 'wt_cache_';

interface CacheEntry {
  data: WaitTimeResult;
  timestamp: number;
}

const memoryCache = new Map<string, CacheEntry>();

const getStorageKey = (airportCode: string) => `${STORAGE_KEY_PREFIX}${airportCode}`;

export const waitTimeCache = {
  get(airportCode: string): { data: WaitTimeResult; stale: boolean } | null {
    const key = airportCode.toUpperCase();

    // Check memory cache first
    const memEntry = memoryCache.get(key);
    if (memEntry) {
      const age = Date.now() - memEntry.timestamp;
      if (age < MEMORY_TTL_MS) {
        return { data: memEntry.data, stale: false };
      }
      // Memory is stale, but still return it
      return { data: memEntry.data, stale: true };
    }

    // Check localStorage
    try {
      const raw = localStorage.getItem(getStorageKey(key));
      if (raw) {
        const entry: CacheEntry = JSON.parse(raw);
        const age = Date.now() - entry.timestamp;
        if (age < STORAGE_TTL_MS) {
          // Promote to memory cache
          memoryCache.set(key, entry);
          return { data: entry.data, stale: age > MEMORY_TTL_MS };
        }
        // Expired, clean up
        localStorage.removeItem(getStorageKey(key));
      }
    } catch {
      // localStorage unavailable
    }

    return null;
  },

  set(airportCode: string, data: WaitTimeResult): void {
    const key = airportCode.toUpperCase();
    const entry: CacheEntry = { data, timestamp: Date.now() };

    memoryCache.set(key, entry);

    try {
      localStorage.setItem(getStorageKey(key), JSON.stringify(entry));
    } catch {
      // localStorage full or unavailable
    }
  },

  invalidate(airportCode: string): void {
    const key = airportCode.toUpperCase();
    memoryCache.delete(key);
    try {
      localStorage.removeItem(getStorageKey(key));
    } catch {
      // ignore
    }
  },
};
